<template>
  <div class="dean-dashboard pa-10">

    <!-- ── Header ── -->
    <div class="d-flex flex-wrap justify-space-between align-center mb-7 gap-3">
      <div>
        <div class="dash-page-title">Dean Dashboard</div>
        <div class="dash-page-sub mt-1">OJT Program Oversight &amp; Institutional Compliance</div>
      </div>
      <div class="d-flex align-center" style="gap: 12px;">
        <v-select
          v-model="selectedBatchId"
          :items="batches"
          item-title="name"
          item-value="id"
          density="compact"
          variant="outlined"
          hide-details
          class="batch-select"
          style="min-width: 200px;"
        />
        <div class="date-pill rounded-lg px-4 py-2">{{ currentDate }}</div>
        <v-btn
          color="#10b981"
          variant="flat"
          prepend-icon="mdi-download-outline"
          class="text-white"
          rounded="lg"
          @click="showReportsDialog = true"
        >
          Generate Reports
        </v-btn>
      </div>
    </div>

    <GenerateReportsDialog
      v-if="showReportsDialog"
      @close="showReportsDialog = false"
    />

    <!-- ── Stat Cards ── -->
    <v-row class="mb-5" style="gap: 0;">
      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--green rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--green rounded-lg pa-2">
              <v-icon size="22" color="#059669">mdi-account-group-outline</v-icon>
            </div>
            <v-icon size="18" color="#059669">mdi-trending-up</v-icon>
          </div>
          <div class="stat-number stat-number--green">
            {{ stats.deployment_status.deployed }}/{{ stats.deployment_status.total }}
          </div>
          <div class="stat-label stat-label--green">Deployment Status</div>
          <div class="stat-sub">Students currently deployed</div>
        </div>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--blue rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--blue rounded-lg pa-2">
              <v-icon size="22" color="#2563eb">mdi-file-document-outline</v-icon>
            </div>
            <v-icon size="18" color="#2563eb">mdi-trending-up</v-icon>
          </div>
          <div class="stat-number stat-number--blue">{{ stats.active_moas }}</div>
          <div class="stat-label stat-label--blue">Active MOAs</div>
          <div class="stat-sub">Valid partnership agreements</div>
        </div>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--purple rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--purple rounded-lg pa-2">
              <v-icon size="22" color="#7c3aed">mdi-office-building-outline</v-icon>
            </div>
            <v-icon size="18" color="#7c3aed">mdi-trending-up</v-icon>
          </div>
          <div class="stat-number stat-number--purple">{{ stats.active_htes }}</div>
          <div class="stat-label stat-label--purple">Active HTEs</div>
          <div class="stat-sub">Partner organizations</div>
        </div>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--teal rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--teal rounded-lg pa-2">
              <v-icon size="22" color="#0d9488">mdi-check-circle-outline</v-icon>
            </div>
            <v-icon size="18" color="#0d9488">mdi-check-decagram</v-icon>
          </div>
          <div class="stat-number stat-number--teal">{{ stats.compliance_rate }}%</div>
          <div class="stat-label stat-label--teal">Compliance Rate</div>
          <div class="stat-sub">Overall deployment compliance</div>
        </div>
      </v-col>
    </v-row>

    <!-- ── Charts Row ── -->
    <v-row class="mb-5">
      <v-col cols="12" md="6">
        <div class="dash-card rounded-xl pa-6" style="height: 100%;">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="card-title">Deployment Status by Program</div>
            <v-icon size="20" color="#94a3b8">mdi-chart-bar</v-icon>
          </div>
          <div class="card-sub mb-5">Student deployment progress</div>
          <div class="chart-wrap">
            <canvas ref="deploymentByProgramChart"></canvas>
          </div>
        </div>
      </v-col>

      <v-col cols="12" md="6">
        <div class="dash-card rounded-xl pa-6" style="height: 100%;">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="card-title">MOA Compliance Report</div>
            <v-icon size="20" color="#94a3b8">mdi-file-sign</v-icon>
          </div>
          <div class="card-sub mb-5">Partnership agreements status overview</div>
          <div class="chart-wrap chart-wrap--doughnut">
            <canvas ref="moaComplianceChart"></canvas>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- ── Interns Finishing Soon + HTE Records ── -->
    <v-row class="mb-5">
      <!-- Interns Finishing Soon -->
      <v-col cols="12" md="6">
        <div class="dash-card rounded-xl pa-6" style="height: 100%;">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="card-title">Interns Finishing Soon</div>
            <v-icon size="20" color="#059669">mdi-clock-outline</v-icon>
          </div>
          <div class="card-sub mb-4">Based on DTR monitoring (≥90% completion)</div>

          <div v-if="interns_finishing_soon.length === 0" class="empty-state">
            No interns at 90%+ completion yet.
          </div>

          <div
            v-for="intern in interns_finishing_soon"
            :key="intern.name"
            class="intern-row rounded-lg pa-3 mb-2"
          >
            <div class="d-flex justify-space-between align-center mb-1">
              <div class="intern-name">{{ intern.name }}</div>
              <div
                class="pct-badge rounded"
                :class="intern.percentage >= 100 ? 'pct-badge--complete' : 'pct-badge--near'"
              >
                {{ intern.percentage }}%
              </div>
            </div>
            <div class="intern-sub">
              {{ intern.hours_logged }} / {{ intern.required_hours }} hours
              <span class="mx-2">·</span>
              {{ intern.hours_remaining }} hrs remaining
            </div>
          </div>
        </div>
      </v-col>

      <!-- HTE Records & Accreditation -->
      <v-col cols="12" md="6">
        <div class="dash-card rounded-xl pa-6" style="height: 100%;">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="card-title">HTE Records &amp; Accreditation</div>
            <v-icon size="20" color="#94a3b8">mdi-domain</v-icon>
          </div>
          <div class="card-sub mb-4">Active HTEs and accreditation status</div>

          <v-row style="gap: 0;">
            <v-col cols="6" class="pr-2 pb-2">
              <div class="hte-box hte-box--blue rounded-xl pa-4">
                <div class="hte-num hte-num--blue">{{ hte_records.active_htes }}</div>
                <div class="hte-label hte-label--blue">Active HTEs</div>
                <div class="hte-sub">Accepting interns</div>
              </div>
            </v-col>
            <v-col cols="6" class="pl-2 pb-2">
              <div class="hte-box hte-box--green rounded-xl pa-4">
                <div class="hte-num hte-num--green">{{ hte_records.accredited }}</div>
                <div class="hte-label hte-label--green">Accredited</div>
                <div class="hte-sub">Fully compliant</div>
              </div>
            </v-col>
            <v-col cols="6" class="pr-2 pt-2">
              <div class="hte-box hte-box--amber rounded-xl pa-4">
                <div class="hte-num hte-num--amber">{{ hte_records.pending_review }}</div>
                <div class="hte-label hte-label--amber">Pending Review</div>
                <div class="hte-sub">Under evaluation</div>
              </div>
            </v-col>
            <v-col cols="6" class="pl-2 pt-2">
              <div class="hte-box hte-box--red rounded-xl pa-4">
                <div class="hte-num hte-num--red">{{ hte_records.renewal_needed }}</div>
                <div class="hte-label hte-label--red">Renewal Needed</div>
                <div class="hte-sub">Action required</div>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>

    <!-- ── Accreditation Requirements & Reports ── -->
    <div class="dash-card rounded-xl pa-6 mb-5">
      <div class="card-title mb-5">Accreditation Requirements &amp; Reports</div>
      <v-row>
        <v-col cols="12" sm="4">
          <div class="acred-card acred-card--green rounded-xl pa-5">
            <div class="d-flex justify-space-between align-center mb-3">
              <div class="acred-label">Student Deployment Rate</div>
              <v-icon size="18" color="#059669">mdi-trending-up</v-icon>
            </div>
            <div class="acred-value acred-value--green">{{ accreditation.deployment_rate }}%</div>
            <div class="acred-sub mt-2">{{ accreditation.requirements_compliance.count }} of {{ accreditation.requirements_compliance.total }} students deployed</div>
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="acred-card acred-card--blue rounded-xl pa-5">
            <div class="d-flex justify-space-between align-center mb-3">
              <div class="acred-label">Requirements Compliance</div>
              <v-icon size="18" color="#2563eb">mdi-check-circle-outline</v-icon>
            </div>
            <div class="acred-value acred-value--blue">{{ accreditation.requirements_compliance.rate }}%</div>
            <div class="acred-sub mt-2">{{ accreditation.requirements_compliance.count }} of {{ accreditation.requirements_compliance.total }} students</div>
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="acred-card acred-card--purple rounded-xl pa-5">
            <div class="d-flex justify-space-between align-center mb-3">
              <div class="acred-label">Partner Retention Rate</div>
              <v-icon size="18" color="#7c3aed">mdi-office-building-outline</v-icon>
            </div>
            <div class="acred-value acred-value--purple">{{ accreditation.partner_retention.rate }}%</div>
            <div class="acred-sub mt-2">{{ accreditation.partner_retention.renewing }} of {{ accreditation.partner_retention.total }} HTEs renewing</div>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- ── Recent Activity & Alerts ── -->
    <div class="dash-card rounded-xl pa-6">
      <div class="d-flex align-center gap-3 mb-5">
        <v-icon color="#f59e0b" size="20">mdi-bell-outline</v-icon>&nbsp;
        <div class="card-title">Recent Activity &amp; Alerts</div>
      </div>

      <!-- Monitoring reports pending -->
      <div
        class="alert-item rounded-xl pa-4 mb-3"
        style="background: #fefce8; border: 1px solid #fef08a;"
      >
        <div class="d-flex align-start" style="gap: 16px;">
          <div class="alert-icon-box rounded-lg pa-2" style="background: #fef3c7;">
            <v-icon color="#ca8a04" size="20">mdi-file-document-outline</v-icon>
          </div>
          <div>
            <div class="alert-title">Monitoring Reports Pending</div>
            <div class="alert-desc">
              {{ alerts.monitoring_reports_pending }} consolidated monitoring report{{ alerts.monitoring_reports_pending !== 1 ? 's' : '' }} require approval
            </div>
            <div class="alert-time">
              <v-icon size="12" color="#9ca3af">mdi-clock-outline</v-icon>
              <span class="ml-1">1 hour ago</span>
            </div>
          </div>
        </div>
      </div>

      <!-- MOA renewal required -->
      <div
        class="alert-item rounded-xl pa-4 mb-3"
        style="background: #fff7ed; border: 1px solid #fed7aa;"
      >
        <div class="d-flex align-start" style="gap: 16px;">
          <div class="alert-icon-box rounded-lg pa-2" style="background: #fde8cc;">
            <v-icon color="#ea580c" size="20">mdi-calendar-alert</v-icon>
          </div>
          <div>
            <div class="alert-title">MOA Renewal Required</div>
            <div class="alert-desc">
              {{ alerts.moa_renewal_required }} partnership agreement{{ alerts.moa_renewal_required !== 1 ? 's' : '' }} expire within 60 days
            </div>
            <div class="alert-time">
              <v-icon size="12" color="#9ca3af">mdi-clock-outline</v-icon>
              <span class="ml-1">1 day ago</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Interns completing -->
      <div
        class="alert-item rounded-xl pa-4"
        style="background: #eff6ff; border: 1px solid #bfdbfe;"
      >
        <div class="d-flex align-start" style="gap: 16px;">
          <div class="alert-icon-box rounded-lg pa-2" style="background: #dbeafe;">
            <v-icon color="#2563eb" size="20">mdi-account-check-outline</v-icon>
          </div>
          <div>
            <div class="alert-title">{{ alerts.interns_completing }} Interns Completing Soon</div>
            <div class="alert-desc">
              Students reaching OJT hour requirement based on DTR monitoring
            </div>
            <div class="alert-time">
              <v-icon size="12" color="#9ca3af">mdi-clock-outline</v-icon>
              <span class="ml-1">2 hours ago</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Recent Activity ── -->
    <div class="dash-card rounded-xl pa-6 mt-5">
      <div class="d-flex justify-space-between align-center mb-4">
        <div>
          <div class="card-title">Recent Activity</div>
          <div class="card-sub">Latest actions across your institute</div>
        </div>
        <router-link to="/dean/activity-log" class="view-all-link">
          View All →
        </router-link>
      </div>

      <div v-if="activityLoading" class="d-flex justify-center py-6">
        <v-progress-circular indeterminate color="#10b981" size="28" />
      </div>

      <div v-else-if="!recentActivity.length" class="text-center py-6">
        <div style="color:#9ca3af; font-size:13px;">No recent activity</div>
      </div>

      <div v-else>
        <div
          v-for="log in recentActivity"
          :key="log.id"
          class="d-flex align-start py-3"
          style="border-bottom: 1px solid #f3f4f6;"
        >
          <div
            class="rounded-lg d-flex align-center justify-center mr-3 flex-shrink-0"
            :style="getActivityBadgeStyle(log.action)"
            style="width:34px; height:34px; margin-top:1px;"
          >
            <v-icon size="15" :color="getActivityColor(log.action)">{{ getActivityIcon(log.action) }}</v-icon>
          </div>
          <div class="flex-grow-1">
            <div class="d-flex justify-space-between align-start" style="gap:8px;">
              <span style="font-size:13px; font-weight:500; color:#111827;">{{ log.description || log.action }}</span>
              <span style="font-size:11px; color:#9ca3af; white-space:nowrap; flex-shrink:0;">{{ formatActivityTime(log.created_at) }}</span>
            </div>
            <div style="font-size:11.5px; color:#9ca3af; margin-top:2px;">{{ log.performed_by_name || '' }}</div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Chart from "chart.js/auto";
import GenerateReportsDialog from "@/components/Dialogs/Forms/GenerateReportsDialog.vue";

export default {
  name: "DeanDashboard",
  components: { GenerateReportsDialog },
  data() {
    return {
      showReportsDialog: false,
      charts: {},
      loading: false,
      batches: [],
      selectedBatchId: null,
      stats: {
        deployment_status: { deployed: 0, total: 0 },
        active_moas: 0,
        active_htes: 0,
        compliance_rate: 0,
      },
      deployment_by_program: [],
      moa_compliance: { active: 0, expiring_soon: 0, pending_review: 0, expired: 0 },
      interns_finishing_soon: [],
      hte_records: {
        active_htes: 0,
        accredited: 0,
        pending_review: 0,
        renewal_needed: 0,
      },
      accreditation: {
        deployment_rate: 0,
        requirements_compliance: { rate: 0, count: 0, total: 0 },
        partner_retention: { rate: 0, renewing: 0, total: 0 },
      },
      alerts: {
        monitoring_reports_pending: 0,
        moa_renewal_required: 0,
        interns_completing: 0,
      },
      recentActivity: [],
      activityLoading: false,
    };
  },
  computed: {
    currentDate() {
      return new Date().toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
  },
  watch: {
    selectedBatchId(val) {
      this.fetchDashboardData(val);
    },
  },
  mounted() {
    this.fetchBatches();
    this.fetchRecentActivity();
  },
  beforeUnmount() {
    Object.values(this.charts).forEach((c) => c && c.destroy());
  },
  methods: {
    async fetchBatches() {
      try {
        const res = await this.axiosCall("/program/getProgramsBatchByInstitute", "GET");
        if (res?.data?.length) {
          this.batches = res.data;
          this.selectedBatchId = res.data[0].id;
        } else {
          this.fetchDashboardData(null);
        }
      } catch (e) {
        console.error("Failed to load batches", e);
        this.fetchDashboardData(null);
      }
    },

    async fetchDashboardData(batchId) {
      this.loading = true;
      const query = batchId ? `?batch=${batchId}` : "";
      try {
        const res = await this.axiosCall(`/student/dean-dashboard${query}`, "GET");
        if (res?.data?.stats) {
          const d = res.data;
          this.stats = d.stats;
          this.deployment_by_program = d.deployment_by_program ?? [];
          this.moa_compliance = d.moa_compliance ?? this.moa_compliance;
          this.interns_finishing_soon = d.interns_finishing_soon ?? [];
          this.hte_records = d.hte_records ?? this.hte_records;
          this.accreditation = d.accreditation ?? this.accreditation;
          this.alerts = d.alerts ?? this.alerts;
        }
      } catch (e) {
        console.error("Failed to load dean dashboard", e);
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.initDeploymentByProgramChart();
          this.initMoaComplianceChart();
        });
      }
    },

    initDeploymentByProgramChart() {
      if (this.charts.deploymentByProgram)
        this.charts.deploymentByProgram.destroy();
      const labels = this.deployment_by_program.map((d) => d.program);
      const deployed = this.deployment_by_program.map((d) => d.deployed);
      const total = this.deployment_by_program.map((d) => d.total);
      this.charts.deploymentByProgram = new Chart(
        this.$refs.deploymentByProgramChart,
        {
          type: "bar",
          data: {
            labels: labels.length ? labels : [],
            datasets: [
              {
                label: "Deployed",
                data: deployed.length ? deployed : [],
                backgroundColor: "#10b981",
                borderRadius: 4,
                barPercentage: 0.5,
              },
              {
                label: "Total",
                data: total.length ? total : [],
                backgroundColor: "#e5e7eb",
                borderRadius: 4,
                barPercentage: 0.5,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: "bottom",
                labels: {
                  usePointStyle: true,
                  pointStyle: "circle",
                  padding: 16,
                  font: { size: 11 },
                  color: "#6b7280",
                },
              },
            },
            scales: {
              x: {
                grid: { display: false },
                ticks: { color: "#9ca3af", font: { size: 11 } },
                border: { display: false },
              },
              y: {
                beginAtZero: true,
                grid: { color: "#f3f4f6" },
                ticks: { color: "#9ca3af", font: { size: 11 } },
                border: { display: false },
              },
            },
          },
        }
      );
    },

    initMoaComplianceChart() {
      if (this.charts.moaCompliance) this.charts.moaCompliance.destroy();
      const { active, expiring_soon, pending_review, expired } = this.moa_compliance;
      this.charts.moaCompliance = new Chart(this.$refs.moaComplianceChart, {
        type: "doughnut",
        data: {
          labels: ["Active", "Expiring Soon", "Pending Review", "Expired"],
          datasets: [
            {
              data: [active, expiring_soon, pending_review, expired],
              backgroundColor: ["#10b981", "#f59e0b", "#3b82f6", "#ec4899"],
              borderWidth: 3,
              borderColor: "#fff",
              hoverOffset: 6,
            },
          ],
        },
        options: {
          cutout: "70%",
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                usePointStyle: true,
                pointStyle: "circle",
                padding: 16,
                font: { size: 11 },
                color: "#6b7280",
              },
            },
          },
        },
      });
    },

    async fetchRecentActivity() {
      this.activityLoading = true;
      try {
        const res = await this.axiosCall("/system-logs?limit=5&page=1", "GET");
        if (res?.data?.data) {
          this.recentActivity = res.data.data;
        }
      } catch (e) {
        console.error("Failed to load recent activity", e);
      } finally {
        this.activityLoading = false;
      }
    },

    getActivityConfig(action) {
      const map = {
        USER_LOGIN:              { color: "#059669", bg: "#ecfdf5", border: "#a7f3d0", icon: "mdi-login-variant" },
        USER_LOGOUT:             { color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb", icon: "mdi-logout-variant" },
        BATCH_CREATED:           { color: "#059669", bg: "#ecfdf5", border: "#a7f3d0", icon: "mdi-layers-plus" },
        STUDENT_ENDORSED:        { color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe", icon: "mdi-send-outline" },
        STUDENT_DEPLOYED:        { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", icon: "mdi-check-circle-outline" },
        STUDENT_PULLED_OUT:      { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", icon: "mdi-account-arrow-left-outline" },
        STUDENT_REASSIGNED:      { color: "#d97706", bg: "#fffbeb", border: "#fde68a", icon: "mdi-account-switch-outline" },
        REQUIREMENT_SUBMITTED:   { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", icon: "mdi-file-upload-outline" },
        REQUIREMENT_APPROVED:    { color: "#059669", bg: "#ecfdf5", border: "#a7f3d0", icon: "mdi-file-check-outline" },
        REQUIREMENT_DISAPPROVED: { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", icon: "mdi-file-cancel-outline" },
        ATTENDANCE_SCANNED:      { color: "#0d9488", bg: "#f0fdfa", border: "#99f6e4", icon: "mdi-qrcode-scan" },
      };
      return map[action] ?? { color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb", icon: "mdi-history" };
    },
    getActivityColor(action) { return this.getActivityConfig(action).color; },
    getActivityIcon(action)  { return this.getActivityConfig(action).icon; },
    getActivityBadgeStyle(action) {
      const c = this.getActivityConfig(action);
      return `background:${c.bg}; border:1px solid ${c.border};`;
    },
    formatActivityTime(val) {
      if (!val) return "";
      const d = new Date(val);
      return d.toLocaleString("en-PH", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    },
  },
};
</script>

<style scoped>
.dean-dashboard {
  min-height: 100vh;
}

/* ── Header ── */
.batch-select :deep(.v-field) {
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  background: #fff;
  --v-field-border-opacity: 1;
}
.batch-select :deep(.v-field__input),
.batch-select :deep(.v-select__selection-text) {
  font-size: 13.5px;
  font-weight: 600;
  color: #374151;
}
.batch-select :deep(.v-field__outline__start),
.batch-select :deep(.v-field__outline__notch),
.batch-select :deep(.v-field__outline__end) {
  border-color: #e2e8f0 !important;
}
.batch-select :deep(.v-field__outline__start) { border-radius: 10px 0 0 10px; }
.batch-select :deep(.v-field__outline__end)   { border-radius: 0 10px 10px 0; }
.batch-select :deep(.v-field--focused .v-field__outline__start),
.batch-select :deep(.v-field--focused .v-field__outline__notch),
.batch-select :deep(.v-field--focused .v-field__outline__end) {
  border-color: #e2e8f0 !important;
}

.dash-page-title {
  font-size: 30px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
  line-height: 1.2;
}
.dash-page-sub {
  font-size: 14px;
  color: #6b7280;
}
.date-pill {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  white-space: nowrap;
}

/* ── Stat Cards ── */
.stat-card {
  border: 1px solid transparent;
  transition: box-shadow 0.2s;
  height: 100%;
}
.stat-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.09);
}
.stat-card--green  { background: #ecfdf5; border-color: #a7f3d0; }
.stat-card--blue   { background: #eff6ff; border-color: #bfdbfe; }
.stat-card--purple { background: #f5f3ff; border-color: #ddd6fe; }
.stat-card--teal   { background: #f0fdfa; border-color: #99f6e4; }

.stat-icon-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.stat-icon-box--green  { background: #a7f3d0; }
.stat-icon-box--blue   { background: #bfdbfe; }
.stat-icon-box--purple { background: #ddd6fe; }
.stat-icon-box--teal   { background: #99f6e4; }

.stat-number {
  font-size: 36px;
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -1px;
}
.stat-number--green  { color: #059669; }
.stat-number--blue   { color: #2563eb; }
.stat-number--purple { color: #7c3aed; }
.stat-number--teal   { color: #0d9488; }

.stat-label {
  font-size: 13px;
  font-weight: 600;
  margin-top: 6px;
}
.stat-label--green  { color: #059669; }
.stat-label--blue   { color: #2563eb; }
.stat-label--purple { color: #7c3aed; }
.stat-label--teal   { color: #0d9488; }

.stat-sub {
  font-size: 11.5px;
  color: #9ca3af;
  margin-top: 3px;
}

/* ── Dash Cards ── */
.dash-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
}
.card-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}
.card-sub {
  font-size: 12.5px;
  color: #9ca3af;
}

/* ── Charts ── */
.chart-wrap {
  position: relative;
  width: 100%;
  height: 240px;
}
.chart-wrap--doughnut {
  height: 220px;
}

/* ── Interns Finishing Soon ── */
.intern-row {
  background: #f9fafb;
  border: 1px solid #f3f4f6;
}
.intern-name {
  font-size: 13.5px;
  font-weight: 700;
  color: #111827;
}
.intern-sub {
  font-size: 11.5px;
  color: #9ca3af;
  margin-top: 2px;
}
.pct-badge {
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
}
.pct-badge--complete {
  background: #d1fae5;
  color: #059669;
}
.pct-badge--near {
  background: #e0f2fe;
  color: #0369a1;
}
.empty-state {
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
  padding: 24px 0;
}

/* ── HTE Records ── */
.hte-box {
  border: 1px solid transparent;
  height: 100%;
}
.hte-box--blue   { background: #eff6ff; border-color: #bfdbfe; }
.hte-box--green  { background: #ecfdf5; border-color: #a7f3d0; }
.hte-box--amber  { background: #fffbeb; border-color: #fde68a; }
.hte-box--red    { background: #fff7ed; border-color: #fed7aa; }

.hte-num {
  font-size: 32px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -1px;
}
.hte-num--blue   { color: #2563eb; }
.hte-num--green  { color: #059669; }
.hte-num--amber  { color: #d97706; }
.hte-num--red    { color: #ea580c; }

.hte-label {
  font-size: 13px;
  font-weight: 600;
  margin-top: 4px;
}
.hte-label--blue   { color: #2563eb; }
.hte-label--green  { color: #059669; }
.hte-label--amber  { color: #d97706; }
.hte-label--red    { color: #ea580c; }

.hte-sub {
  font-size: 11.5px;
  color: #9ca3af;
  margin-top: 2px;
}

/* ── Accreditation ── */
.acred-card {
  border: 1px solid transparent;
  height: 100%;
  transition: box-shadow 0.2s;
}
.acred-card:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.07);
}
.acred-card--green  { background: #ecfdf5; border-color: #a7f3d0; }
.acred-card--blue   { background: #eff6ff; border-color: #bfdbfe; }
.acred-card--purple { background: #f5f3ff; border-color: #ddd6fe; }

.acred-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #6b7280;
}
.acred-value {
  font-size: 36px;
  font-weight: 800;
  letter-spacing: -1px;
  line-height: 1.1;
}
.acred-value--green  { color: #059669; }
.acred-value--blue   { color: #2563eb; }
.acred-value--purple { color: #7c3aed; }
.acred-sub {
  font-size: 12px;
  color: #6b7280;
}

/* ── Alerts ── */
.alert-icon-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.alert-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 2px;
}
.alert-desc {
  font-size: 12.5px;
  color: #6b7280;
  margin-bottom: 4px;
}
.alert-time {
  font-size: 11.5px;
  color: #9ca3af;
  display: flex;
  align-items: center;
}
.view-all-link {
  font-size: 13px;
  color: #10b981;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
}
.view-all-link:hover {
  text-decoration: underline;
}
</style>
